"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ShoppingCart, CreditCard, ChevronUp, ChevronDown, Volume2, VolumeX, Loader2 } from "lucide-react";
import { useCart } from "../app/context/CartContext";
import { useRouter } from "next/navigation";

interface ShoppableVideo {
  id: number;
  title: string;
  youtubeVideoId: string;
  description: string;
  displayOrder: number;
  productId?: number;
  productName?: string;
  productPrice?: number;
  productSlug?: string;
  productThumbnail?: string;
}

interface YouTubePlayerProps {
  videoId: string;
  isActive: boolean;
  isMuted: boolean;
}

const YouTubePlayer = ({ videoId, isActive, isMuted }: YouTubePlayerProps) => {
  if (!isActive) {
    return (
      <div className="w-full h-full bg-black flex items-center justify-center">
        <img 
          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} 
          alt="Preview" 
          className="w-full h-full object-cover opacity-60 animate-fade-in"
        />
      </div>
    );
  }

  const src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${isMuted ? 1 : 0}&loop=1&playlist=${videoId}&controls=0&playsinline=1&modestbranding=1&showinfo=0&rel=0`;

  return (
    <iframe
      src={src}
      title="Shoppable Video Player"
      className="w-full h-full border-0 absolute inset-0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    />
  );
};

export default function ShoppableVideoFeed() {
  const { addToCart } = useCart();
  const router = useRouter();

  const [videos, setVideos] = useState<ShoppableVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeVideoId, setActiveVideoId] = useState<number | null>(null);
  const [currentVisibleVideoId, setCurrentVisibleVideoId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const isInitialScrollRef = useRef(true);
  const productApi = process.env.NEXT_PUBLIC_PRODUCT_API || 'http://localhost:8081';

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await fetch(`${productApi}/api/videos`);
        if (res.ok) {
          const data = await res.json();
          setVideos(data || []);
        }
      } catch (err) {
        console.error("Failed to load shoppable videos:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchVideos();
  }, [productApi]);

  const handleScrollToNext = () => {
    const currentIndex = videos.findIndex((v) => v.id === currentVisibleVideoId);
    if (currentIndex !== -1 && currentIndex < videos.length - 1) {
      const nextVideo = videos[currentIndex + 1];
      const element = document.getElementById(`video-section-${nextVideo.id}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleScrollToPrev = () => {
    const currentIndex = videos.findIndex((v) => v.id === currentVisibleVideoId);
    if (currentIndex > 0) {
      const prevVideo = videos[currentIndex - 1];
      const element = document.getElementById(`video-section-${prevVideo.id}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleAddToCart = (video: ShoppableVideo) => {
    if (!video.productId) return;
    try {
      addToCart({
        id: video.productId,
        name: video.productName || video.title,
        price: video.productPrice || 0,
        image: video.productThumbnail || `https://img.youtube.com/vi/${video.youtubeVideoId}/hqdefault.jpg`,
      });
      setToastMessage(`${video.productName || video.title} added to cart!`);
      setTimeout(() => setToastMessage(null), 3000);
    } catch (err) {
      console.error(err);
    }
  };

  const handleBuyNow = (video: ShoppableVideo) => {
    if (!video.productId) return;
    try {
      addToCart({
        id: video.productId,
        name: video.productName || video.title,
        price: video.productPrice || 0,
        image: video.productThumbnail || `https://img.youtube.com/vi/${video.youtubeVideoId}/hqdefault.jpg`,
      });
      router.push("/checkout");
    } catch (err) {
      console.error(err);
    }
  };

  // Scroll to active video once when modal opens
  useEffect(() => {
    if (activeVideoId !== null) {
      isInitialScrollRef.current = true;
      setCurrentVisibleVideoId(activeVideoId);
      
      const scrollTimer = setTimeout(() => {
        const element = document.getElementById(`video-section-${activeVideoId}`);
        if (element) {
          element.scrollIntoView({ behavior: "auto" });
        }
      }, 50);

      const enableObserverTimer = setTimeout(() => {
        isInitialScrollRef.current = false;
      }, 500);

      return () => {
        clearTimeout(scrollTimer);
        clearTimeout(enableObserverTimer);
      };
    } else {
      isInitialScrollRef.current = true;
    }
  }, [activeVideoId]);

  // Set up IntersectionObserver to track visible video in scroll container
  useEffect(() => {
    if (activeVideoId === null || videos.length === 0) {
      setCurrentVisibleVideoId(null);
      return;
    }

    const observerOptions = {
      root: scrollRef.current,
      rootMargin: "0px",
      threshold: 0.6,
    };

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      if (isInitialScrollRef.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const idStr = entry.target.getAttribute("data-video-id");
          if (idStr) {
            setCurrentVisibleVideoId(parseInt(idStr, 10));
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    videos.forEach((video) => {
      const el = document.getElementById(`video-section-${video.id}`);
      if (el) {
        observer.observe(el);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [activeVideoId, videos]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveVideoId(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (activeVideoId !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [activeVideoId]);

  if (isLoading) {
    return (
      <section className="py-16 w-full max-w-7xl mx-auto px-4 sm:px-6 text-center">
        <Loader2 className="w-8 h-8 text-mid-green animate-spin mx-auto mb-4" />
        <p className="text-gray-500 font-medium">Loading shoppable feeds...</p>
      </section>
    );
  }

  if (videos.length === 0) {
    return null; // Don't render anything if there are no videos configured.
  }

  return (
    <section className="py-16 w-full max-w-7xl mx-auto px-4 sm:px-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-3 mb-8 border-b border-border pb-4">
        <div className="text-left">
          <span className="text-amber font-bold text-[10px] uppercase tracking-widest block mb-1">Interactive Showcase</span>
          <h2 className="text-3xl font-display font-black text-deep-green leading-tight">
            Shoppable Video Feeds
          </h2>
        </div>
        <div className="text-xs font-black text-amber bg-amber/10 px-4 py-2 rounded-full border border-amber/20 uppercase tracking-wider">
          ⚡ Tap to Watch & Shop
        </div>
      </div>

      {/* Horizontal Scrollable Feed */}
      <div className="relative w-full">
        <div className="flex overflow-x-auto gap-4 sm:gap-6 pb-8 pt-4 hide-scrollbar snap-x snap-mandatory">
          {videos.map((video) => {
            const coverImage = `https://img.youtube.com/vi/${video.youtubeVideoId}/hqdefault.jpg`;
            return (
              <motion.div
                key={video.id}
                whileHover={{ y: -8 }}
                className="relative flex-none w-[200px] h-[350px] sm:w-[240px] sm:h-[400px] rounded-2xl overflow-hidden cursor-pointer group shadow-md snap-start bg-zinc-100 dark:bg-zinc-800 border border-zinc-200/50"
                onClick={() => video.id && setActiveVideoId(video.id)}
              >
                <img
                  src={coverImage}
                  alt={video.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-opacity group-hover:opacity-90" />

                {/* Play Button Icon */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/20 backdrop-blur-md w-14 h-14 rounded-full flex items-center justify-center border border-white/40 shadow-xl">
                    <Play className="w-6 h-6 text-white ml-1 fill-white" />
                  </div>
                </div>

                {/* Card Details */}
                <div className="absolute bottom-0 left-0 w-full p-4 flex flex-col gap-1">
                  <h3 className="text-white font-bold line-clamp-2 text-sm sm:text-base leading-tight drop-shadow-md">
                    {video.title}
                  </h3>
                  {video.productPrice && (
                    <p className="text-amber font-extrabold text-sm">₹{video.productPrice}</p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Fullscreen Video Modal (Shorts Style) */}
      <AnimatePresence>
        {activeVideoId !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl flex flex-col font-body"
          >
            {/* Modal Header / Controls */}
            <div className="absolute top-0 left-0 w-full p-4 flex justify-between items-center z-50 pointer-events-none">
              <div className="pointer-events-auto flex items-center gap-4">
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-full backdrop-blur-md transition-colors border border-white/10"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
              </div>
              <button
                onClick={() => setActiveVideoId(null)}
                className="pointer-events-auto bg-white/10 hover:bg-white/20 text-white p-3 rounded-full backdrop-blur-md transition-colors border border-white/10"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Scrolling Container for Shorts */}
            <div
              ref={scrollRef}
              className="h-full w-full overflow-y-auto snap-y snap-mandatory hide-scrollbar relative"
            >
              {videos.map((video) => {
                const isActive = currentVisibleVideoId === video.id;

                return (
                  <div
                    key={video.id}
                    id={`video-section-${video.id}`}
                    data-video-id={video.id}
                    className="h-[100dvh] w-full snap-start snap-always flex items-center justify-center p-0 md:p-6 lg:p-12"
                  >
                    {/* Two-Column Layout Container */}
                    <div className="w-full max-w-5xl h-full md:h-[85vh] flex flex-col md:flex-row bg-zinc-950 md:bg-zinc-900/50 md:backdrop-blur-2xl md:rounded-[2rem] overflow-hidden md:border md:border-white/10 shadow-2xl relative">
                      {/* Left: Video Area */}
                      <div className="relative flex-1 h-[60vh] md:h-full bg-black flex items-center justify-center overflow-hidden">
                        <YouTubePlayer
                          videoId={video.youtubeVideoId}
                          isActive={isActive}
                          isMuted={isMuted}
                        />
                        {/* Mobile gradient for readability */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent md:hidden pointer-events-none" />
                      </div>

                      {/* Right: Product Details Area */}
                      <div className="w-full md:w-[380px] lg:w-[420px] bg-white dark:bg-zinc-900 p-6 md:p-8 flex flex-col h-[40vh] md:h-full rounded-t-3xl md:rounded-none -mt-6 md:mt-0 relative z-10 animate-slide-up">
                        <div className="flex-1 overflow-y-auto hide-scrollbar pb-6">
                          {/* Mobile handle indicator */}
                          <div className="w-12 h-1 bg-zinc-300 dark:bg-zinc-700 rounded-full mx-auto mb-6 md:hidden" />

                          <div className="flex items-start justify-between gap-4 mb-3">
                            <h2 className="text-xl md:text-2xl font-display font-black text-zinc-900 dark:text-zinc-50 leading-tight">
                              {video.title}
                            </h2>
                          </div>

                          {video.productId && (
                            <div className="flex items-center gap-3 bg-gray-50 dark:bg-zinc-800/50 border border-gray-100 dark:border-zinc-800 p-3 rounded-2xl mb-5">
                              {video.productThumbnail ? (
                                <img
                                  src={video.productThumbnail}
                                  alt={video.productName}
                                  className="w-12 h-12 rounded-xl object-cover bg-white shadow-sm border border-gray-100"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-xl bg-gray-105" />
                              )}
                              <div className="min-w-0">
                                <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider">Featured Product</span>
                                <span className="block text-sm font-bold text-gray-800 dark:text-gray-200 truncate leading-snug">{video.productName}</span>
                                <span className="block text-sm font-extrabold text-mid-green dark:text-green-400 mt-0.5">₹{video.productPrice}</span>
                              </div>
                            </div>
                          )}

                          <div className="prose prose-zinc dark:prose-invert">
                            <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1.5">
                              Video Story
                            </h4>
                            <p className="text-zinc-600 dark:text-zinc-300 text-sm leading-relaxed">
                              {video.description || "Learn more about our premium organic products by watching this shorts showcase."}
                            </p>
                          </div>
                        </div>

                        {/* Sticky Bottom Actions */}
                        {video.productId && (
                          <div className="pt-4 mt-auto border-t border-zinc-150 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                            <div className="flex flex-col gap-2.5">
                              <button
                                onClick={() => handleAddToCart(video)}
                                className="w-full py-3.5 px-6 rounded-xl bg-mid-green hover:bg-deep-green text-white font-bold text-base flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-md shadow-mid-green/10"
                              >
                                <ShoppingCart className="w-5 h-5" />
                                Add to Cart
                              </button>
                              <button
                                onClick={() => handleBuyNow(video)}
                                className="w-full py-3.5 px-6 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-800 dark:text-white font-bold text-base flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                              >
                                <CreditCard className="w-5 h-5" />
                                Buy Now
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Navigation Arrows */}
            <div className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col gap-4 z-50">
              <button
                onClick={handleScrollToPrev}
                className="bg-white/10 hover:bg-white/20 text-white p-3.5 rounded-full backdrop-blur-md transition-all hover:scale-110 active:scale-95 border border-white/10"
              >
                <ChevronUp className="w-6 h-6" />
              </button>
              <button
                onClick={handleScrollToNext}
                className="bg-white/10 hover:bg-white/20 text-white p-3.5 rounded-full backdrop-blur-md transition-all hover:scale-110 active:scale-95 border border-white/10"
              >
                <ChevronDown className="w-6 h-6" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Local Toast Notification in ShoppableVideoFeed */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-10 right-10 z-[110] bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 border border-emerald-500/30"
          >
            <div className="w-6 h-6 bg-mid-green rounded-full flex items-center justify-center text-white font-bold text-xs shadow-sm">
              ✓
            </div>
            <span className="font-bold text-sm">{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx global>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}
